/**
 * Pi Adapter
 * 
 * Adapts the Pi Network SDK for the application.
 * Encapsulates all interactions with window.Pi to provide a consistent interface
 * and decouple the application logic from the specific SDK implementation.
 * 
 * Future-proofing: This adapter can be swapped or extended for other payment providers.
 */

const PiAdapter = {
    // 🎯 [NEW] 초기화 상태 추적
    _initialized: false,
    _initConfig: null,

    // Check if SDK is loaded
    isReady: () => {
        return !!window.Pi;
    },

    // 🎯 [NEW] init() 호출 여부 확인
    isInitialized: () => {
        return PiAdapter._initialized && !!window.Pi;
    },

    getEnvironment: () => {
        return window.Pi ? window.Pi.environment : null;
    },

    getUser: () => {
        return window.Pi && window.Pi.user ? window.Pi.user : null;
    },

    /**
     * Initialize the Pi SDK
     * @param {string} version - SDK version (e.g., "2.0")
     * @param {boolean} sandbox - Whether to run in sandbox mode
     * @param {string} appId - The Pi App ID (optional in some versions but good practice)
     */
    init: ({ version = "2.0", sandbox = false, appId = null }) => {
        if (!window.Pi) {
            console.warn("[PiAdapter] Pi SDK not found during init.");
            return;
        }
        try {
            PiAdapter._initConfig = { version, sandbox, appId };
            window.Pi.init({ version, sandbox, appId });
            PiAdapter._initialized = true;
            console.log(`[PiAdapter] Initialized (Sandbox: ${sandbox})`);
        } catch (e) {
            console.error("[PiAdapter] Initialization failed:", e);
            throw e;
        }
    },

    /**
     * 🎯 [NEW] SDK가 로드되어 있지만 init()이 호출되지 않은 경우 자동 초기화
     * (SDK 지연 로드 시 Race Condition 방지)
     */
    ensureInitialized: () => {
        if (!window.Pi) return false;
        if (PiAdapter._initialized) return true;
        console.warn("[PiAdapter] SDK loaded but not initialized — auto-initializing");
        try {
            const config = PiAdapter._initConfig || { version: "2.0", sandbox: false };
            window.Pi.init(config);
            PiAdapter._initialized = true;
            return true;
        } catch (e) {
            console.error("[PiAdapter] Auto-init failed:", e);
            return false;
        }
    },

    /**
     * Authenticate the user
     * @param {Array<string>} scopes - Requested scopes (e.g., ['username', 'payments'])
     * @param {Function} onIncompletePaymentFound - Callback for incomplete payments
     * @returns {Promise<object>} - Resolves with auth result { accessToken, user: { uid, username, ... } }
     */
    authenticate: async (scopes, onIncompletePaymentFound) => {
        if (!window.Pi) throw new Error("Pi SDK not loaded");

        // 🎯 [NEW] 초기화 안 되어 있으면 자동 초기화 시도
        if (!PiAdapter._initialized) {
            const ok = PiAdapter.ensureInitialized();
            if (!ok) throw new Error("Pi SDK initialization failed");
        }

        try {
            const authResult = await window.Pi.authenticate(scopes, onIncompletePaymentFound);
            return authResult;
        } catch (e) {
            console.error("[PiAdapter] Authentication failed:", e);
            throw e;
        }
    },

    /**
     * Create a Payment
     * Wraps the callback-based createPayment in a Promise pattern where possible,
     * but typically Pi SDK control flow relies on callbacks for server-side approval steps.
     * 
     * @param {object} paymentData - { amount, memo, metadata }
     * @param {object} callbacks - { onReadyForServerApproval, onReadyForServerCompletion, onCancel, onError }
     */
    createPayment: (paymentData, callbacks) => {
        if (!window.Pi) throw new Error("Pi SDK not loaded");

        window.Pi.createPayment(paymentData, {
            onReadyForServerApproval: callbacks.onReadyForServerApproval,
            onReadyForServerCompletion: callbacks.onReadyForServerCompletion,
            onCancel: (paymentId) => {
                console.warn("[PiAdapter] Payment Cancelled:", paymentId);
                if (callbacks.onCancel) callbacks.onCancel(paymentId);
            },
            onError: (error, payment) => {
                console.error("[PiAdapter] Payment Error:", error, payment);
                if (callbacks.onError) callbacks.onError(error, payment);
            }
        });
    },

    /**
     * Open Share Dialog (if supported by platform)
     * @param {string} title 
     * @param {string} message 
     */
    openShareDialog: (title, message) => {
        if (window.Pi && window.Pi.openShareDialog) {
            window.Pi.openShareDialog(title, message);
        } else {
            console.warn("[PiAdapter] Share dialog not supported");
            // Fallback or ignore
        }
    },

    /**
     * Show a Rewarded Ad (SDK: showAd(adType) with "rewarded" | "interstitial" only)
     * @param {string} adId - 앱 내 구분용 ID (ad_ticket_reward, ad_star_reward 등). SDK에는 "rewarded" 전달.
     * @returns {Promise<void>} - Resolves when ad is fully watched (AD_REWARDED), rejects otherwise
     * @see https://github.com/pi-apps/pi-platform-docs/blob/master/SDK_reference.md#ads
     */
    showAd: (adId) => {
        return new Promise((resolve, reject) => {
            if (!window.Pi || !window.Pi.Ads) {
                console.warn("[PiAdapter] Pi Ads SDK not loaded or not supported.");
                if (process.env.NODE_ENV === 'development' || document.domain === 'localhost') {
                    console.log("[PiAdapter] Simulating Ad (Localhost)...");
                    setTimeout(() => {
                        console.log("[PiAdapter] Auto-simulating ad completion (Dev Mode)");
                        resolve();
                    }, 1000);
                    return;
                }
                reject(new Error("AD_SHOW_FAILED: ADS_NOT_SUPPORTED"));
                return;
            }

            try {
                // SDK 시그니처: showAd(adType: "interstitial" | "rewarded") — 타입만 허용
                const adType = "rewarded";
                window.Pi.Ads.showAd(adType)
                    .then((res) => {
                        if (res && res.result === "AD_REWARDED") {
                            console.log("[PiAdapter] Ad watched successfully (AD_REWARDED).");
                            resolve();
                        } else if (res && res.result === "AD_CLOSED") {
                            reject(new Error("AD_CLOSED_BY_USER"));
                        } else {
                            const result = (res && res.result) || "AD_UNKNOWN";
                            reject(new Error(`AD_SHOW_FAILED: ${result}`));
                        }
                    })
                    .catch((error) => {
                        console.error("[PiAdapter] Ad Failed/Cancelled:", error);
                        reject(error);
                    });
            } catch (e) {
                console.error("[PiAdapter] Ad Exception:", e);
                reject(e);
            }
        });
    }
};

export default PiAdapter;
